/**
 * QR Customer Menu PWA — Müşteri kendi telefonundan masada sipariş verir.
 *
 * URL: /?t=<qr_token> ile masa belirlenir.
 * Akış: Landing → (opt OTP login) → Menü (2 katmanlı tab) → Ürün detay
 *        → Sepet → Sipariş Takip
 *
 * Tasarım: referans QR menü mockup'ı —
 *  - Üst tab: kök kategoriler (Yiyecekler / İçecekler)
 *  - Alt tab: seçili kökün alt kategorileri (Burgerler / Pizzalar...)
 *  - Resimli ürün kartları, ürün detay modalı (galeri + içindekiler + kalori)
 *  - Tema rengi tenant.brand_color'dan (admin Ayarlar > Brand Color).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Stack,
  Card,
  Group,
  Text,
  Title,
  Button,
  ActionIcon,
  Badge,
  Drawer,
  Divider,
  Center,
  ScrollArea,
  Loader,
  Alert,
  Avatar,
  Image,
  Box,
  Modal,
  Container,
  SimpleGrid,
  Checkbox,
} from "@mantine/core";
import {
  IconShoppingCart, IconPlus, IconMinus, IconAlertCircle,
  IconUser, IconPhoto, IconFlame, IconChevronLeft, IconReceipt, IconTrash,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import axios from "axios";
import {
  useResolveQrToken,
  usePublicMenu,
  useCreateQrOrder,
  useCancelQrRequest,
  useTableOrders,
  useRequestQrPayment,
} from "../../shared/api";
import { useQueryClient } from "@tanstack/react-query";

const MENU_API_URL = import.meta.env["VITE_API_URL"] || "http://localhost:8000";
import { useI18n, type Locale } from "../../shared/i18n";
import { useWebSocket } from "../../shared/ws";
import { CustomerLoginModal } from "./CustomerAuth";
import { MyAccountDrawer } from "./MyAccount";
import { ReservationModal } from "./Reservation";

type Step = "landing" | "menu" | "tracking";
type CartItem = {
  // Sepet satır kimliği: aynı ürün farklı modifier seçimiyle ayrı satır
  // olur — `productId|sorted(modifierIds)` biçiminde.
  id: string;
  productId: string;
  name: string;
  price: number; // birim fiyat + seçili modifier fiyatları (kuruş)
  qty: number;
  image?: string | null;
  description?: string;
  modifierIds: string[];
  modifierNames: string[];
};

const DEFAULT_BRAND = "#228be6";

function safeBrand(color?: string | null): string {
  if (color && /^#[0-9a-fA-F]{6}$/.test(color)) return color;
  return DEFAULT_BRAND;
}

// Fiyat ve sipariş butonlarının vurgu rengi — tenant ayarı boş/geçersizse marka rengi.
function safeAccent(color: string | null | undefined, brand: string): string {
  return color && /^#[0-9a-fA-F]{6}$/.test(color) ? color : brand;
}

// Elemanın canlı yüksekliği (ResizeObserver) — sticky satırların üst ofseti
// sabit sayı yerine gerçek başlık yüksekliğinden hesaplansın.
function useElementHeight(initial: number): [(el: HTMLElement | null) => void, number] {
  const [height, setHeight] = useState(initial);
  const observer = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el) return;
    const measure = () => setHeight(Math.round(el.getBoundingClientRect().height));
    measure();
    observer.current = new ResizeObserver(measure);
    observer.current.observe(el);
  }, []);
  return [ref, height];
}

function shade(hex: string, percent: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.min(255, ((n >> 16) & 0xff) + percent));
  const g = Math.max(0, Math.min(255, ((n >> 8) & 0xff) + percent));
  const b = Math.max(0, Math.min(255, (n & 0xff) + percent));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function getTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("t") || params.get("token");
}

function formatTRY(minor: number): string {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
  }).format(minor / 100);
}

// ---------------------------------------------------------------------------
// Görselsiz öğeler için placeholder
// ---------------------------------------------------------------------------
function ImagePlaceholder({ h, w }: { h: number; w?: number | string }) {
  return (
    <Center
      h={h}
      w={w}
      style={{
        background: "linear-gradient(135deg,#f1f3f5,#e9ecef)",
        borderRadius: "var(--mantine-radius-md)",
        flexShrink: 0,
      }}
    >
      <IconPhoto size={h > 80 ? 26 : 18} color="var(--mantine-color-gray-5)" />
    </Center>
  );
}

// ===========================================================================
// Landing
// ===========================================================================
// QR menü dil değiştirici — attığın tasarımlardaki TR/EN rozet grubu.
// localStorage + URL'i useI18n yönetir; payload ?lang ile yeniden çekilir.
function LangSwitcher({ langs = ["tr", "en"] as Locale[] }: { langs?: Locale[] }) {
  const { lang, setLang } = useI18n();
  return (
    <Group gap={4}>
      {langs.map((l) => {
        const active = lang === l;
        // Mavi header üzerinde: SEÇİLİ dil = dolu beyaz buton (net görünür),
        // seçili olmayan = şeffaf/yarı-saydam beyaz çerçeveli.
        return (
          <Button
            key={l}
            size="compact-xs"
            radius="xl"
            onClick={() => setLang(l)}
            aria-pressed={active}
            styles={{
              root: {
                backgroundColor: active ? "white" : "transparent",
                color: active ? "var(--qr-brand, #2563eb)" : "white",
                border: active ? "none" : "1px solid rgba(255,255,255,0.6)",
                fontWeight: active ? 700 : 500,
              },
            }}
          >
            {l.toUpperCase()}
          </Button>
        );
      })}
    </Group>
  );
}

function Landing({
  token, brand, logo, background, onStart, onReservation,
}: {
  token: string;
  brand: string;
  logo?: string | null;
  background?: string | null;
  onStart: () => void;
  onReservation: () => void;
}) {
  const { t } = useI18n();
  const { data, isLoading, isError } = useResolveQrToken(token);

  if (isLoading) {
    return (
      <Center mih="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (isError || !data) {
    return (
      <Center mih="100vh" p="md">
        <Alert color="red" icon={<IconAlertCircle size={20} />} title={t("qr.invalid", "Geçersiz QR")}>
          {t("qr.invalid_desc", "QR kod tanınmadı. Lütfen garsondan yardım isteyin.")}
        </Alert>
      </Center>
    );
  }

  // Arka plan: her zaman marka gradyanı tabanda durur (görsel yüklenemese/
  // şeffaf olsa bile boş kalmaz). Görsel varsa onun üstüne `cover` ile basılır
  // ve okunabilirlik için koyu overlay eklenir. Görsel ayrı bir katmandadır —
  // böylece kart içeriği her zaman net görünür, görsel hiçbir zaman taşmaz.
  const gradient = `linear-gradient(160deg, ${brand}, ${shade(brand, -55)})`;

  // Admin ayarları (Sistem Ayarları > QR):
  // - hideName: logo varsa restoran adını gizle (çift markayı önler)
  // - cardBlur: kart yarı saydam + buzlu cam → arka plan görseli kartın
  //   arkasından bulanık ama görünür geçer
  const hideName = !!data.tenant?.hide_name_when_logo && !!logo;
  const cardBlur = !!data.tenant?.card_blur && !!background;
  // Rezervasyon butonu admin ayarıyla gizlenebilir (varsayılan açık).
  const showReservation = data.tenant?.show_reservation !== false;
  // Saydamlık: admin ayarı (0–100) → alpha (0–1). Geçersiz/eksikse 62.
  // Bulanıklık (blur) saydamlıkla ORANTILI: %0 → 0px (cam tamamen berrak,
  // hiç blur yok), %100 → 16px (tam buzlu cam). Böylece tek ayar hem
  // saydamlığı hem bulanıklığı kontrol eder; 0'da hiç efekt kalmaz.
  const rawOpacity = data.tenant?.card_opacity;
  const opacityPct = Number.isFinite(rawOpacity)
    ? Math.min(100, Math.max(0, rawOpacity))
    : 62;
  const alpha = opacityPct / 100;
  const blurPx = Math.round((opacityPct / 100) * 16);
  const cardStyle = cardBlur
    ? {
        background: `rgba(255,255,255,${alpha})`,
        ...(blurPx > 0
          ? { backdropFilter: `blur(${blurPx}px)`, WebkitBackdropFilter: `blur(${blurPx}px)` }
          : {}),
        // Blur açıkken kart arka planla erimesin — belirgin kenar + güçlü gölge
        // (özellikle landscape'te kartın net bir kutu gibi durması için).
        border: "1px solid rgba(255,255,255,0.55)",
        boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
      }
    : undefined;

  // Logo renklendirme (CSS mask): tek renk + şeffaf zeminli logoyu seçilen
  // renge boyar. Kapalıysa orijinal logo gösterilir.
  const logoTint =
    !!data.tenant?.logo_tint_enabled && !!data.tenant?.logo_tint_color
      ? data.tenant.logo_tint_color
      : null;

  return (
    <Box
      style={{
        position: "relative",
        minHeight: "100vh",
        background: gradient,
        // Landscape (yatay) / kısa ekranda kart sığmazsa dikey kaydırma.
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {/* Landscape'te kart daha dar + net dursun (arka planla erimesin) ve
          kısa ekranda içerik sığsın: logo küçülür, boşluk daralır.
          Mantine Card'a media query veremediğimiz için scoped CSS. */}
      <style>{`
        .qr-landing-card { width: 100%; max-width: 400px; }
        /* img logo: oran korunur (max-height) — mask'lı logo: sabit height */
        .qr-landing-logo { max-height: 180px; }
        @media (orientation: landscape) {
          .qr-landing-card { max-width: 360px; }
          .qr-landing-card .qr-landing-stack { gap: var(--mantine-spacing-sm); }
          .qr-landing-logo { max-height: 96px; }
          .qr-landing-logo-mask { height: 96px !important; }
        }
        /* Çok kısa ekran (yatay telefon) — daha da kompakt */
        @media (max-height: 480px) {
          .qr-landing-logo { max-height: 84px; }
          .qr-landing-logo-mask { height: 84px !important; }
          .qr-landing-card .qr-landing-stack { gap: var(--mantine-spacing-xs); }
        }
      `}</style>
      {/* Arka plan görseli katmanı — sticky/fixed değil: içerik scroll olunca
          arka plan da uzar, taşmaz. */}
      {background && (
        <Box
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(160deg, rgba(0,0,0,0.35), rgba(0,0,0,0.65)), url(${background})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
      )}

      {/* İçerik katmanı — arka planın üstünde. Dil değiştirici sabit köşede,
          kart akış içinde dikeyde ortalanır ama uzunsa kesilmez (flex+padding). */}
      <Box style={{ position: "relative", zIndex: 1, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <Group justify="flex-end" p="md">
          <LangSwitcher />
        </Group>
        <Box
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            // Kart içeriği ekranı aşarsa üst/alttan nefes payı + kaydırma
            padding: "var(--mantine-spacing-md)",
          }}
        >
          <Card
            className="qr-landing-card"
            padding="xl"
            radius="lg"
            shadow="lg"
            style={cardStyle}
          >
            {/* Ad gizliyse logo ile içerik arası daha sıkı (boşluk azalır).
                Landscape/kısa ekranda gap ayrıca CSS ile küçülür. */}
            <Stack className="qr-landing-stack" gap={hideName ? "xs" : "lg"} align="center">
              {logo ? (
                logoTint ? (
                  // Renklendirme açık: logoyu CSS mask ile seçilen renge boya.
                  // Tek renk + şeffaf zeminli logoda çalışır (silhouette).
                  <Box
                    aria-label={data.tenant.name}
                    className="qr-landing-logo-mask"
                    style={{
                      width: "100%",
                      maxWidth: 320,
                      height: 180,
                      backgroundColor: logoTint,
                      WebkitMaskImage: `url(${logo})`,
                      maskImage: `url(${logo})`,
                      WebkitMaskRepeat: "no-repeat",
                      maskRepeat: "no-repeat",
                      WebkitMaskPosition: "center",
                      maskPosition: "center",
                      WebkitMaskSize: "contain",
                      maskSize: "contain",
                    }}
                  />
                ) : (
                  // Logo ölçeği sabit kutuya hapsedilir — büyük görsel taşmaz,
                  // oranı korunur (object-fit: contain).
                  <img
                    src={logo}
                    alt={data.tenant.name}
                    className="qr-landing-logo"
                    style={{
                      width: "100%",
                      maxWidth: 320,
                      height: "auto",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                )
              ) : (
                <Text size="3rem">🍴</Text>
              )}
              {/* Logo varken ad gizlenebilir (admin ayarı) — çift marka önlenir.
                  Gizliyse hiç render edilmez, boşluk da bırakmaz. */}
              {!hideName && (
                <Title order={2} ta="center">
                  {data.tenant.name}
                </Title>
              )}
              <Badge size="xl" style={{ background: brand }}>
                {data.table.name}
              </Badge>
              <Text c="dimmed" ta="center" size="sm">
                {t("qr.landing_hint", "Menüye göz atın ve siparişinizi telefonunuzdan verin")}
              </Text>
              <Button size="xl" fullWidth onClick={onStart} style={{ background: brand }}>
                {t("qr.open_menu", "Menüyü Aç")}
              </Button>
              {/* Rezervasyon butonu admin ayarıyla gizlenebilir */}
              {showReservation && (
                <Button size="lg" variant="default" fullWidth onClick={onReservation}>
                  📅 {t("qr.reservation", "Rezervasyon Yap")}
                </Button>
              )}
            </Stack>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}

interface CustomerSession {
  customer_token: string;
  customer_id: string;
  display_name: string;
  phone: string;
  avatar?: string;
}

// ---------------------------------------------------------------------------
// Kategori ağacı — payload düz liste (parent_id ile); kök + çocuk ayır
// ---------------------------------------------------------------------------
interface CatNode {
  id: string;
  name: string;
  parent_id: string | null;
  image?: string | null;
  image_thumbnail?: string | null;
}

function buildTree(categories: CatNode[]) {
  const roots = categories.filter((c) => !c.parent_id);
  const childrenOf = (rootId: string) =>
    categories.filter((c) => c.parent_id === rootId);
  return { roots, childrenOf };
}

// ===========================================================================
// Ürün detay modalı — galeri (max 4) + içindekiler + kalori + alerjenler
// ===========================================================================
function ProductDetailModal({
  product, brand, accent, onClose, onAdd,
}: {
  product: any | null;
  brand: string;
  accent: string;
  onClose: () => void;
  onAdd: (p: any, modifierIds: string[]) => void;
}) {
  const { t } = useI18n();
  const [activeImg, setActiveImg] = useState(0);
  // Görsel galerisinin kaydırılabilir kabı — nokta tıklamasıyla programlı scroll için.
  const galleryRef = useRef<HTMLDivElement>(null);
  // Seçili modifier id'leri — modal her açıldığında (ürün değişince) sıfırlanır.
  const [selectedMods, setSelectedMods] = useState<string[]>([]);

  useEffect(() => {
    setActiveImg(0);
    setSelectedMods([]);
    // Ürün değişince galeriyi başa sar.
    galleryRef.current?.scrollTo({ left: 0 });
  }, [product?.id]);

  if (!product) return null;

  const images: any[] = product.images?.length
    ? product.images
    : product.image
    ? [{ image: product.image, thumbnail: product.image_thumbnail }]
    : [];
  const allergens: string[] = product.allergens || [];
  // Aktif modifier'lar — backend get_public_menu her ürünle birlikte döndürür.
  const modifiers: any[] = product.modifiers || [];
  // Seçili modifier fiyat toplamı — buton üzerinde toplam tutarı göstermek için.
  const modsTotal = selectedMods.reduce((sum: number, mid: string) => {
    const m = modifiers.find((x) => x.id === mid);
    return sum + (m?.price_minor || 0);
  }, 0);

  return (
    <Modal opened={!!product} onClose={onClose} title={product.name} size="md" radius="md">
      <Stack gap="md">
        {/* Görsel galerisi — mobilde yatay swipe ile kaydırılır, altta nokta göstergeleri */}
        {images.length > 0 ? (
          <Box>
            <Box
              ref={galleryRef}
              onScroll={(e) => {
                // Kaydırma bittiğinde hangi görselin ortada olduğunu hesapla.
                const el = e.currentTarget;
                const idx = Math.round(el.scrollLeft / el.clientWidth);
                if (idx !== activeImg) setActiveImg(idx);
              }}
              style={{
                display: "flex",
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                scrollbarWidth: "none",
                borderRadius: 8,
                WebkitOverflowScrolling: "touch",
              }}
            >
              {images.map((img, i) => (
                <Box
                  key={i}
                  style={{ flex: "0 0 100%", scrollSnapAlign: "center" }}
                >
                  <Image
                    src={img.image}
                    h={240}
                    radius="md"
                    fit="cover"
                    alt={`${product.name} ${i + 1}`}
                  />
                </Box>
              ))}
            </Box>

            {/* Nokta göstergeleri (birden fazla görsel varsa) */}
            {images.length > 1 && (
              <Group justify="center" gap={6} mt={8}>
                {images.map((_, i) => (
                  <Box
                    key={i}
                    onClick={() => {
                      const el = galleryRef.current;
                      if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
                      setActiveImg(i);
                    }}
                    style={{
                      cursor: "pointer",
                      height: 8,
                      width: i === activeImg ? 20 : 8,
                      borderRadius: 4,
                      backgroundColor: i === activeImg ? brand : "var(--mantine-color-gray-3)",
                      transition: "width 150ms ease, background-color 150ms ease",
                    }}
                  />
                ))}
              </Group>
            )}
          </Box>
        ) : (
          <ImagePlaceholder h={240} w="100%" />
        )}

        {/* Fiyat + kalori */}
        <Group justify="space-between">
          <Text fw={800} size="xl" style={{ color: accent }}>
            {formatTRY(product.unit_price_minor || 0)}
          </Text>
          {product.calories > 0 && (
            <Badge
              size="lg"
              variant="light"
              color="orange"
              leftSection={<IconFlame size={14} />}
            >
              {product.calories} kcal
            </Badge>
          )}
        </Group>

        {/* Açıklama */}
        {product.description && (
          <Text size="sm" c="dimmed">{product.description}</Text>
        )}

        {/* İçindekiler */}
        {product.ingredients_text && (
          <Box>
            <Text fw={700} size="sm" mb={4}>{t("qr.ingredients", "İçindekiler")}</Text>
            <Text size="sm" c="dimmed">{product.ingredients_text}</Text>
          </Box>
        )}

        {/* Alerjenler */}
        {allergens.length > 0 && (
          <Box>
            <Text fw={700} size="sm" mb={6}>{t("qr.allergen_warning", "Alerjen Uyarısı")}</Text>
            <Group gap={6}>
              {allergens.map((a) => (
                <Badge key={a} size="sm" color="red" variant="light">
                  {a}
                </Badge>
              ))}
            </Group>
          </Box>
        )}

        {/* Modifier'lar — ekstra/seçenek (peynir, sos, boyut...). Opsiyonel;
            seçilenlerin fiyatı birim fiyata eklenir. */}
        {modifiers.length > 0 && (
          <Box>
            <Text fw={700} size="sm" mb={6}>{t("qr.extras", "Ekstra Seçenekler")}</Text>
            <Stack gap={6}>
              {modifiers.map((m) => (
                <Checkbox
                  key={m.id}
                  checked={selectedMods.includes(m.id)}
                  onChange={(e) => {
                    // e.currentTarget'ı state-updater'ın İÇİNDE okumak null
                    // verir (sentetik olay updater çalışmadan geri dönüştürülür).
                    // Değeri burada, senkron olarak yakala.
                    const isChecked = e.currentTarget.checked;
                    setSelectedMods((prev) =>
                      isChecked
                        ? [...prev, m.id]
                        : prev.filter((x) => x !== m.id),
                    );
                  }}
                  label={
                    <Group justify="space-between" wrap="nowrap" style={{ width: "100%" }}>
                      <Text size="sm">{m.name}</Text>
                      {m.price_minor > 0 && (
                        <Text size="sm" c="dimmed">+{formatTRY(m.price_minor)}</Text>
                      )}
                    </Group>
                  }
                />
              ))}
            </Stack>
          </Box>
        )}

        <Button
          size="lg"
          fullWidth
          style={{ background: accent }}
          leftSection={<IconPlus size={18} />}
          onClick={() => {
            onAdd(product, selectedMods);
            onClose();
          }}
        >
          {t("qr.add_to_cart", "Sepete Ekle")} · {formatTRY((product.unit_price_minor || 0) + modsTotal)}
        </Button>
      </Stack>
    </Modal>
  );
}

// ===========================================================================
// Kategori grid kartı — referans tasarım (resimli, kategori adı altta)
// ===========================================================================
function CategoryCard({
  cat, brand, productCount, onClick,
}: {
  cat: CatNode;
  brand: string;
  productCount: number;
  onClick: () => void;
}) {
  const { t } = useI18n();
  const thumb = cat.image_thumbnail || cat.image;
  return (
    <Card
      padding={0}
      radius="md"
      withBorder
      style={{ cursor: "pointer", overflow: "hidden" }}
      onClick={onClick}
    >
      {thumb ? (
        <Image src={thumb} h={120} fit="cover" alt={cat.name} />
      ) : (
        <ImagePlaceholder h={120} w="100%" />
      )}
      {/* Kategori adı şeridi — brand renkli (referanstaki gibi) */}
      <Box px="sm" py={6} style={{ background: brand }}>
        <Text fw={700} size="sm" c="white" lineClamp={1}>
          {cat.name}
        </Text>
        {productCount > 0 && (
          <Text size="10px" c="white" opacity={0.85}>
            {t("qr.items_count", "{n} ürün").replace("{n}", String(productCount))}
          </Text>
        )}
      </Box>
    </Card>
  );
}

// ===========================================================================
// Menü — 3 seviyeli: kök grid → alt grid → ürün listesi (2 satır tab)
// ===========================================================================
function MenuView({
  token,
  brand,
  accent,
  cart,
  setCart,
  onCheckout,
  customer,
  onLogin,
  onAccount,
  onShowOrders,
}: {
  token: string;
  brand: string;
  accent: string;
  cart: CartItem[];
  setCart: (c: CartItem[]) => void;
  onCheckout: () => void;
  customer: CustomerSession | null;
  onLogin: () => void;
  onLogout: () => void;
  onAccount: () => void;
  onShowOrders: () => void;
}) {
  const { lang, t } = useI18n();
  const { data, isLoading } = usePublicMenu(token, lang);
  const menu = data || { categories: [], products: [], table: {}, tenant: {} };

  const products = useMemo(() => menu.products || [], [menu]);
  const categories: CatNode[] = useMemo(() => menu.categories || [], [menu]);

  const { roots, childrenOf } = useMemo(
    () => buildTree(categories),
    [categories],
  );

  // Navigasyon — iki ekran:
  //  "browse"   → kök şerit (biri hep seçili) + seçili kökün alt kategori grid'i
  //  "products" → seçili alt kategorinin ürün listesi + 2 satır tab
  // activeRoot:  ekranda seçili kök kategori (browse'da hep dolu — ilk kök default)
  // activeChild: seçili alt kategori (null → browse grid; dolu → ürün listesi)
  const [activeRoot, setActiveRoot] = useState<string | null>(null);
  const [activeChild, setActiveChild] = useState<string | null>(null);
  // Browse şeridinde seçili kök kartı — geri dönüldüğünde slider'ı bu karta
  // kaydırıp görünür kılmak için (Tatlılar gibi listenin ilerisindeki kökler
  // aksi halde sahne dışında kalıyor).
  const activeRootCardRef = useRef<HTMLDivElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailProduct, setDetailProduct] = useState<any | null>(null);
  // Sticky üst bar + kök sekme satırının gerçek yükseklikleri (kaydırırken
  // sekmeler başlığın altında kalmasın, arkasına gizlenmesin).
  const [headerRef, headerH] = useElementHeight(96);
  const [rootTabsRef, rootTabsH] = useElementHeight(38);

  // İlk yüklemede ilk kök seçili gelir (referans: "Yiyecekler" default)
  useEffect(() => {
    if (roots.length > 0 && !activeRoot) {
      setActiveRoot(roots[0].id);
    }
  }, [roots, activeRoot]);

  // Bir kategorinin (ve alt kategorilerinin) toplam ürün sayısı
  function productCountFor(catId: string): number {
    const childIds = childrenOf(catId).map((c) => c.id);
    const ids = [catId, ...childIds];
    return products.filter((p: any) => ids.includes(p.category_id)).length;
  }

  // Ağaç yoksa (tüm kategoriler düz/kök) — kök grid doğrudan ürünlere götürür
  const hasTree = roots.some((r) => childrenOf(r.id).length > 0);

  function addToCart(p: any, modifierIds: string[] = []) {
    const productModifiers: any[] = p.modifiers || [];
    const selected = productModifiers.filter((m) =>
      modifierIds.includes(m.id),
    );
    const modsTotal = selected.reduce(
      (sum: number, m: any) => sum + (m.price_minor || 0),
      0,
    );
    const basePrice = p.unit_price_minor || p.price_minor || 0;
    // Satır kimliği: aynı ürünün farklı modifier seçimi ayrı satır olur.
    const sortedIds = [...modifierIds].sort();
    const lineId = sortedIds.length
      ? `${p.id}|${sortedIds.join(",")}`
      : p.id;
    const existing = cart.find((c) => c.id === lineId);
    if (existing) {
      setCart(cart.map((c) => (c.id === lineId ? { ...c, qty: c.qty + 1 } : c)));
    } else {
      setCart([
        ...cart,
        {
          id: lineId,
          productId: p.id,
          name: p.name,
          price: basePrice + modsTotal,
          qty: 1,
          image: p.image_thumbnail || p.image || null,
          description: p.description || p.ingredients_text || "",
          modifierIds: sortedIds,
          modifierNames: selected.map((m: any) => m.name),
        },
      ]);
    }
    notifications.show({
      message: t("qr.added_to_cart", "{name} sepete eklendi").replace("{name}", p.name),
      color: "green",
      autoClose: 1500,
    });
  }

  function changeQty(id: string, delta: number) {
    setCart(
      cart
        .map((c) => (c.id === id ? { ...c, qty: c.qty + delta } : c))
        .filter((c) => c.qty > 0),
    );
  }

  function removeItem(id: string) {
    setCart(cart.filter((c) => c.id !== id));
  }

  const total = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const itemCount = cart.reduce((s, c) => s + c.qty, 0);

  // --- Navigasyon yardımcıları ---
  // Browse ekranındaki kök şeridi — köke tıklandı, browse'da kalır, alt grid
  // değişir. Köke doğrudan ürün bağlıysa (alt kategori yok) ürün listesine geç.
  function selectRoot(rootId: string) {
    setActiveRoot(rootId);
    const kids = childrenOf(rootId);
    setActiveChild(kids.length > 0 ? null : rootId);
  }
  // Ürün listesi ekranındaki üst tab — köke tıklandı, BU EKRANDA KAL.
  // Kökün ilk alt kategorisinin ürünlerini doğrudan göster (browse'a dönme).
  function selectRootStayInProducts(rootId: string) {
    setActiveRoot(rootId);
    const kids = childrenOf(rootId);
    setActiveChild(kids.length > 0 ? kids[0].id : rootId);
  }
  // Alt kategori kartına/tab'ına tıklandı → ürün listesi ekranı
  function openChild(childId: string) {
    setActiveChild(childId);
  }
  // Ürün listesinden geri → browse (alt kategori grid'i).
  // Seçili kökün alt kategorisi yoksa (ürünler doğrudan köke bağlı) browse
  // grid'i boş "alt grup yok" gösterirdi — bu durumda alt-kategorili ilk köke
  // dön ki kullanıcı boş bir ekranla karşılaşmasın.
  function backToBrowse() {
    if (activeRoot && childrenOf(activeRoot).length === 0) {
      const firstBranchRoot = roots.find((r) => childrenOf(r.id).length > 0);
      if (firstBranchRoot) {
        setActiveRoot(firstBranchRoot.id);
      }
    }
    setActiveChild(null);
  }

  // Görünüm: activeChild null → browse (kök şerit + alt grid)
  //          activeChild dolu → ürün listesi + 2 satır tab
  const showProducts = !!activeChild;

  // Browse şeridine girildiğinde / seçili kök değiştiğinde, seçili kök kartını
  // slider içinde görünür hale getir. Aksi halde Tatlılar gibi listenin
  // ilerisindeki kökler işaretli olsa da sahne dışında kalıyor.
  useEffect(() => {
    if (showProducts) return;
    activeRootCardRef.current?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [showProducts, activeRoot]);

  // Yükleme ekranı TÜM hook'lardan sonra — öncesinde erken dönüş hook sayısını
  // değiştirir ve dil değişiminde React #300 ile menü çöker (beyaz ekran).
  if (isLoading) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }

  const childCats = activeRoot ? childrenOf(activeRoot) : [];
  const filteredProducts = products.filter(
    (p: any) => p.category_id === activeChild,
  );
  const activeRootNode = roots.find((r) => r.id === activeRoot);
  // Seçili kökün alt kategorisi yok ama doğrudan ürünü var mı? (Kahvaltılar gibi
  // düz kökler — browse grid'inde "alt grup yok" yerine ürünleri açtırmak için.)
  // Kökün DOĞRUDAN kendisine atanmış ürün sayısı (alt grupları hariç).
  const rootOwnProductCount = activeRoot
    ? products.filter((p: any) => p.category_id === activeRoot).length
    : 0;
  const rootHasOwnProducts = rootOwnProductCount > 0;

  // Başlık — browse'da restoran adı, ürün listesinde kök kategori adı
  const headerTitle = showProducts
    ? (activeRootNode?.name || t("qr.menu", "Menü"))
    : (menu.tenant?.name || t("qr.menu", "Menü"));

  return (
    <Box pb={120} mih="100vh" bg="gray.0">
      {/* Üst bar — iki satır:
          1) Başlık (sol) · Giriş/Hesap (sağ)
          2) TR/EN dil değiştirici (sol) · masa + siparişlerim (sağ) */}
      <Box ref={headerRef} px="md" py="sm" style={{ background: brand, position: "sticky", top: 0, zIndex: 60 }}>
        <Stack gap={8}>
          {/* Satır 1 — başlık + Giriş aynı hizada */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
              {showProducts && (
                <ActionIcon
                  variant="white"
                  size="md"
                  onClick={backToBrowse}
                  aria-label={t("qr.back", "Geri")}
                >
                  <IconChevronLeft size={18} />
                </ActionIcon>
              )}
              <Text fw={700} c="white" size="lg" lineClamp={1}>
                {headerTitle}
              </Text>
            </Group>
            {customer ? (
              <Avatar
                size={38}
                radius="xl"
                onClick={onAccount}
                src={customer.avatar || null}
                imageProps={{ onError: (e: any) => { e.currentTarget.style.display = "none"; } }}
                aria-label={t("qr.account", "Hesabım")}
                style={{ cursor: "pointer", background: "white", color: brand, fontWeight: 700, border: "2px solid white" }}
              >
                {(customer.display_name?.[0] || customer.phone?.slice(-2, -1) || "?").toUpperCase()}
              </Avatar>
            ) : (
              <Button size="xs" variant="white" leftSection={<IconUser size={14} />} onClick={onLogin}>
                {t("qr.login", "Giriş")}
              </Button>
            )}
          </Group>

          {/* Satır 2 — dil (sol) · masa + siparişlerim (sağ) */}
          <Group justify="space-between" wrap="nowrap">
            <LangSwitcher />
            <Group gap="xs" wrap="nowrap">
              <Badge variant="white" size="lg">{menu.table?.name || t("qr.table", "Masa")}</Badge>
              <ActionIcon
                variant="white"
                size="lg"
                onClick={onShowOrders}
                aria-label={t("qr.my_orders", "Siparişlerim")}
              >
                <IconReceipt size={18} color={brand} />
              </ActionIcon>
            </Group>
          </Group>
        </Stack>
      </Box>

      {/* === BROWSE: kök şerit (resimli, biri seçili) + alt kategori grid'i === */}
      {!showProducts && (
        <>
          {/* Kök kategori şeridi — küçük resimli kartlar (referans tasarım) */}
          {roots.length > 1 && (
            <Box style={{ background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <ScrollArea type="never">
                <Group gap="md" wrap="nowrap" px="md" py="md" align="flex-start">
                  {roots.map((r) => {
                    const active = r.id === activeRoot;
                    const thumb = r.image_thumbnail || r.image;
                    return (
                      <Stack
                        key={r.id}
                        ref={active ? activeRootCardRef : undefined}
                        gap={6}
                        align="center"
                        onClick={() => selectRoot(r.id)}
                        style={{ cursor: "pointer", flexShrink: 0, width: 120 }}
                      >
                        <Box
                          style={{
                            width: 120,
                            height: 120,
                            flexShrink: 0,
                            borderRadius: 8,
                            overflow: "hidden",
                            outline: active ? `3px solid ${brand}` : "3px solid transparent",
                          }}
                        >
                          {thumb ? (
                            <Image
                              src={thumb}
                              h={120}
                              w={120}
                              fit="cover"
                              alt={r.name}
                            />
                          ) : (
                            <ImagePlaceholder h={120} w={120} />
                          )}
                        </Box>
                        <Text
                          size="md"
                          fw={active ? 800 : 600}
                          {...(active ? {} : { c: "dimmed" })}
                          {...(active ? { style: { color: brand } } : {})}
                          lineClamp={1}
                        >
                          {r.name}
                        </Text>
                      </Stack>
                    );
                  })}
                </Group>
              </ScrollArea>
            </Box>
          )}

          {/* Seçili kökün alt kategori grid'i.
              Dar (portrait) 2 sütun; geniş/yatay ekranda 3 sütun → kartlar
              devasa görünmesin, alan ferah kullanılsın. */}
          <Container size="md" p="md">
            <Text fw={600} c="dimmed" size="sm" mb="sm">
              {activeRootNode?.name
                ? t("qr.select_category_of", "{cat} — kategori seçin").replace("{cat}", activeRootNode.name)
                : t("qr.select_category", "Kategori seçin")}
            </Text>
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
              {childCats.map((c) => (
                <CategoryCard
                  key={c.id}
                  cat={c}
                  brand={brand}
                  productCount={productCountFor(c.id)}
                  onClick={() => openChild(c.id)}
                />
              ))}
              {rootHasOwnProducts && (
                // Kökün doğrudan kendi ürünleri (admin'de alt gruba değil köke
                // atanmış) — alt grupları olsa da kaybolmasın, ayrı kart.
                <CategoryCard
                  cat={activeRootNode!}
                  brand={brand}
                  productCount={rootOwnProductCount}
                  onClick={() => openChild(activeRoot!)}
                />
              )}
            </SimpleGrid>
            {childCats.length === 0 && !rootHasOwnProducts && (
              <Text c="dimmed" ta="center" py="xl">
                {roots.length === 0
                  ? t("qr.menu_not_ready", "Menü henüz hazır değil")
                  : t("qr.no_subgroups", "Bu kategoride alt grup yok")}
              </Text>
            )}
          </Container>
        </>
      )}

      {/* === ÜRÜN LİSTESİ: 2 satır tab === */}
      {showProducts && (
        <>
          {/* Üst tab — kök kategoriler */}
          {hasTree && roots.length > 0 && (
            <Box ref={rootTabsRef} style={{ background: brand, position: "sticky", top: headerH, zIndex: 55 }}>
              <ScrollArea type="never">
                <Group gap={0} wrap="nowrap" px="xs">
                  {roots.map((r) => {
                    const active = r.id === activeRoot;
                    return (
                      <Box
                        key={r.id}
                        onClick={() => selectRootStayInProducts(r.id)}
                        px="md"
                        py={8}
                        style={{
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          color: "white",
                          fontWeight: active ? 800 : 500,
                          borderBottom: active ? "3px solid white" : "3px solid transparent",
                          opacity: active ? 1 : 0.75,
                        }}
                      >
                        {r.name}
                      </Box>
                    );
                  })}
                </Group>
              </ScrollArea>
            </Box>
          )}

          {/* Alt tab — kardeş alt kategoriler */}
          {childCats.length > 0 && (
            <Box
              py={8}
              style={{
                background: "white",
                position: "sticky",
                top: hasTree ? headerH + rootTabsH : headerH,
                zIndex: 50,
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              }}
            >
              <ScrollArea type="never">
                <Group gap="xs" wrap="nowrap" px="md">
                  {childCats.map((c) => {
                    const active = c.id === activeChild;
                    return (
                      <Box
                        key={c.id}
                        onClick={() => openChild(c.id)}
                        px="md"
                        py={6}
                        style={{
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          borderRadius: 999,
                          fontSize: 14,
                          fontWeight: active ? 700 : 500,
                          background: active ? brand : "var(--mantine-color-gray-1)",
                          color: active ? "white" : "var(--mantine-color-gray-7)",
                        }}
                      >
                        {c.name}
                      </Box>
                    );
                  })}
                </Group>
              </ScrollArea>
            </Box>
          )}

          {/* Ürün listesi — dar (portrait) tek sütun; geniş/yatay ekranda
              2 sütun (alan ferah kullanılsın). */}
          <Container size="md" p="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              {filteredProducts.map((p: any) => {
                const thumb = p.image_thumbnail || p.image;
                return (
                  <Card
                    key={p.id}
                    padding="sm"
                    radius="md"
                    withBorder
                    style={{ cursor: "pointer" }}
                    onClick={() => setDetailProduct(p)}
                  >
                    <Group wrap="nowrap" align="flex-start">
                      {thumb ? (
                        <Image src={thumb} h={88} w={88} radius="sm" fit="cover" alt={p.name} />
                      ) : (
                        <ImagePlaceholder h={88} w={88} />
                      )}
                      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                        <Group justify="space-between" wrap="nowrap">
                          <Text fw={700} lineClamp={1}>{p.name}</Text>
                          {p.calories > 0 && (
                            <Badge
                              size="xs"
                              variant="light"
                              color="orange"
                              leftSection={<IconFlame size={10} />}
                            >
                              {p.calories}
                            </Badge>
                          )}
                        </Group>
                        {(p.description || p.ingredients_text) && (
                          <Text size="xs" c="dimmed" lineClamp={2}>
                            {p.description || p.ingredients_text}
                          </Text>
                        )}
                        <Group justify="space-between" mt={2}>
                          <Text fw={700} style={{ color: accent }}>
                            {formatTRY(p.unit_price_minor || p.price_minor || 0)}
                          </Text>
                          <ActionIcon
                            size="lg"
                            radius="xl"
                            variant="filled"
                            style={{ background: accent }}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Modifier'lı üründe doğrudan ekleme yapma —
                              // detay modalını aç ki müşteri ekstra seçsin.
                              if (p.modifiers?.length) {
                                setDetailProduct(p);
                              } else {
                                addToCart(p);
                              }
                            }}
                            aria-label={`${p.name} ekle`}
                          >
                            <IconPlus size={18} />
                          </ActionIcon>
                        </Group>
                      </Stack>
                    </Group>
                  </Card>
                );
              })}
            </SimpleGrid>
            {filteredProducts.length === 0 && (
              <Text c="dimmed" ta="center" py="xl">{t("qr.no_products_in_category", "Bu kategoride ürün yok")}</Text>
            )}
          </Container>
        </>
      )}

      {/* Sepet butonu */}
      {itemCount > 0 && (
        <Button
          size="xl"
          radius="xl"
          style={{
            position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)",
            zIndex: 100, boxShadow: "0 4px 12px rgba(0,0,0,0.25)", background: accent,
          }}
          leftSection={<IconShoppingCart size={22} />}
          onClick={() => setDrawerOpen(true)}
        >
          {t("qr.cart_button", "Sepet")} ({itemCount}) — {formatTRY(total)}
        </Button>
      )}

      {/* Ürün detay modalı */}
      <ProductDetailModal
        product={detailProduct}
        brand={brand}
        accent={accent}
        onClose={() => setDetailProduct(null)}
        onAdd={addToCart}
      />

      {/* Sepet drawer'ı — ürün resmi + açıklama ile */}
      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        position="bottom"
        size="80%"
        title={t("qr.cart_title", "Sepetim")}
      >
        <Stack gap="md">
          {cart.map((c) => (
            <Group key={c.id} wrap="nowrap" align="flex-start">
              {c.image ? (
                <Image src={c.image} h={56} w={56} radius="sm" fit="cover" alt={c.name} />
              ) : (
                <ImagePlaceholder h={56} w={56} />
              )}
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Text fw={600} lineClamp={1}>{c.name}</Text>
                {c.modifierNames.length > 0 && (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    + {c.modifierNames.join(", ")}
                  </Text>
                )}
                {c.description && c.modifierNames.length === 0 && (
                  <Text size="xs" c="dimmed" lineClamp={1}>{c.description}</Text>
                )}
                <Text size="xs" c="dimmed">{formatTRY(c.price)} × {c.qty}</Text>
              </Stack>
              <Group gap="xs" style={{ flexShrink: 0 }}>
                <ActionIcon size="lg" variant="default" onClick={() => changeQty(c.id, -1)}>
                  <IconMinus size={16} />
                </ActionIcon>
                <Badge size="lg" variant="filled" style={{ background: brand }}>
                  {c.qty}
                </Badge>
                <ActionIcon size="lg" variant="default" onClick={() => changeQty(c.id, +1)}>
                  <IconPlus size={16} />
                </ActionIcon>
                <ActionIcon
                  size="lg"
                  variant="subtle"
                  color="red"
                  aria-label={t("qr.remove_from_cart", "Ürünü sepetten çıkar")}
                  onClick={() => removeItem(c.id)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Group>
          ))}
          {cart.length === 0 && (
            <Text c="dimmed" ta="center" py="md">{t("qr.cart_empty", "Sepetiniz boş")}</Text>
          )}
          <Divider />
          <Group justify="space-between">
            <Text fw={700} size="lg">{t("qr.total", "Toplam")}</Text>
            <Text fw={700} size="xl" style={{ color: accent }}>{formatTRY(total)}</Text>
          </Group>
          <Button
            size="xl"
            fullWidth
            disabled={cart.length === 0}
            style={{ background: cart.length ? accent : undefined }}
            onClick={onCheckout}
          >
            {t("qr.place_order_full", "Siparişi Ver (Garson Onayı)")}
          </Button>
        </Stack>
      </Drawer>
    </Box>
  );
}

// ===========================================================================
// Sipariş takip — masanın tüm siparişleri + canlı durum + hesap iste
// ===========================================================================
// Takip adımı çeviri key'leri — etiketler render anında t() ile çevrilir.
const TRACK_STEP_KEYS: { key: string; tr: string }[] = [
  { key: "qr.status_received", tr: "Alındı" },
  { key: "qr.status_approved", tr: "Onaylandı" },
  { key: "qr.status_preparing", tr: "Hazırlanıyor" },
  { key: "qr.status_ready", tr: "Hazır" },
];

/** Tek bir siparişin durum ilerleme çubuğu (status_step: 1..4, 0=iptal). */
function OrderProgress({ step, brand }: { step: number; brand: string }) {
  const { t } = useI18n();
  if (step === 0) {
    return <Badge color="red" variant="light">{t("qr.cancelled", "İptal edildi")}</Badge>;
  }
  return (
    <Group gap={4} wrap="nowrap">
      {TRACK_STEP_KEYS.map(({ key, tr }, i) => {
        const label = t(key, tr);
        const idx = i + 1;
        const done = idx <= step;
        return (
          <Stack key={key} gap={2} align="center" style={{ flex: 1 }}>
            <Box
              h={6}
              w="100%"
              style={{
                borderRadius: 3,
                background: done ? brand : "var(--mantine-color-gray-2)",
              }}
            />
            <Text size="9px" {...(done ? {} : { c: "dimmed" })} fw={done ? 700 : 400}>
              {label}
            </Text>
          </Stack>
        );
      })}
    </Group>
  );
}

function Tracking({
  token, brand, onBackToMenu,
}: {
  token: string;
  brand: string;
  onBackToMenu: () => void;
}) {
  // Canlı durum tamamen WebSocket ile. Ekran açılışta veriyi bir kez çeker;
  // sonraki güncellemeler WS push'tan gelir — polling yok (gereksiz çift
  // istek). useWebSocket otomatik reconnect yapar; bağlantı her açıldığında
  // (ilk + her reconnect) query'yi tazeleyerek kopukken kaçan güncellemeyi
  // yakalarız.
  const { t } = useI18n();
  const { data, isLoading } = useTableOrders(token);
  const requestPayment = useRequestQrPayment();
  const cancelRequest = useCancelQrRequest();
  const qc = useQueryClient();

  // QR müşteri WS — masa sipariş durumu değişince anında tazele
  const { lastMessage: wsMessage, status: wsStatus } =
    useWebSocket<any>(`/ws/qr/${token}/`);

  // WS (yeniden) bağlandığında bir kez güncel veriyi çek
  useEffect(() => {
    if (wsStatus === "OPEN") {
      qc.invalidateQueries({ queryKey: ["qr-table-orders", token] });
    }
  }, [wsStatus, qc, token]);

  // WS push — herhangi bir sipariş/ödeme durum event'i geldiğinde tazele
  useEffect(() => {
    if (!wsMessage) return;
    qc.invalidateQueries({ queryKey: ["qr-table-orders", token] });
    const ev = wsMessage.event;
    // qr.request.approved: garson PendingQrRequest'i onayladı (Order yaratıldı).
    // order.confirmed da aynı anda gelir ama table_id taşımaz; bu event müşteri
    // grubuna ulaşan asıl sinyaldir → "garson onayı bekleniyor" çözülür.
    if (ev === "order.confirmed" || ev === "qr.request.approved") {
      notifications.show({
        message: "✅ Siparişiniz garson tarafından onaylandı",
        color: "green",
      });
    } else if (ev === "order.cancelled" || ev === "qr.request.rejected") {
      notifications.show({
        message: "❌ Siparişiniz reddedildi — lütfen garsonunuza danışın",
        color: "red",
        autoClose: false,
      });
    } else if (ev === "order.status_changed") {
      // Mutfak ticket'ı durumu değiştikçe Order.kitchen_status'u
      // CONFIRMED→PREPARING→READY→SERVED ilerletir. Müşteriye sadece
      // anlamlı geçişler bildirilir — PREPARING sessiz (gereksiz spam),
      // READY ve SERVED görünür notification.
      const ks = wsMessage.kitchen_status;
      if (ks === "READY") {
        notifications.show({
          title: "🍽️ Siparişiniz hazır",
          message: "Garson masanıza getiriyor — afiyet olsun!",
          color: "green",
          autoClose: 8000,
        });
      } else if (ks === "SERVED") {
        notifications.show({
          message: "✅ Siparişiniz servis edildi",
          color: "teal",
          autoClose: 5000,
        });
      }
    } else if (ev === "payment.completed") {
      notifications.show({
        message: "💳 Ödemeniz alındı — afiyet olsun!",
        color: "green",
      });
    }
  }, [wsMessage, qc, token]);
  const [payOpen, setPayOpen] = useState(false);

  const orders: any[] = data?.orders || [];
  // "Hesabı İste" yalnızca onaylanmış (gerçek Order) açık siparişleri kapsar.
  // Garson onayı bekleyen pending request'ler ödenemez → hariç tut.
  const openOrders = orders.filter((o) => o.is_open && !o.is_pending_request);
  const openTotal = openOrders.reduce(
    (sum: number, o: any) => sum + (o.total_minor || 0),
    0,
  );

  if (isLoading) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }

  return (
    <Box mih="100vh" bg="gray.0" pb={120}>
      {/* Üst bar */}
      <Box px="md" py="sm" style={{ background: brand, position: "sticky", top: 0, zIndex: 60 }}>
        <Group justify="space-between">
          <Stack gap={2}>
            <Text fw={700} c="white" size="lg">{t("qr.my_orders", "Siparişlerim")}</Text>
            {data?.session?.short_id && (
              <Text size="xs" c="white" opacity={0.85}>
                {t("qr.bill_no", "Adisyon")} #{data.session.short_id}
              </Text>
            )}
          </Stack>
          <Badge variant="white" size="lg">{data?.table?.name || t("qr.table", "Masa")}</Badge>
        </Group>
      </Box>

      <Container size="sm" p="md">
        <Stack gap="md">
          {orders.length === 0 && (
            <Card padding="xl" radius="md" withBorder>
              <Stack align="center" gap="sm">
                <Text fw={600}>{t("qr.no_orders_yet", "Henüz sipariş yok")}</Text>
                <Text size="sm" c="dimmed" ta="center">
                  {t("qr.no_orders_hint", "Menüden seçim yapıp siparişinizi verebilirsiniz.")}
                </Text>
              </Stack>
            </Card>
          )}

          {orders.map((o) => {
            // Garson onayı bekleyen QR request — status_step 0 ama iptal DEĞİL.
            const isPending = o.is_pending_request === true;
            // status_step 0 = iptal/reddedildi — kart soluk + kırmızı vurgu
            // (pending request status_step 0 döndürür ama ayrı işlenir)
            const isCancelled = o.status_step === 0 && !isPending;
            const cardStyle = isPending
              ? { borderColor: "var(--mantine-color-yellow-4)", background: "var(--mantine-color-yellow-0)" }
              : isCancelled
              ? { opacity: 0.7, borderColor: "var(--mantine-color-red-3)" }
              : undefined;
            return (
            <Card
              key={o.id}
              padding="md"
              radius="md"
              withBorder
              style={cardStyle}
            >
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text
                    fw={700}
                    size="sm"
                    style={{ color: isPending ? "var(--mantine-color-yellow-8)" : isCancelled ? "var(--mantine-color-red-7)" : (o.is_open ? brand : undefined) }}
                  >
                    {o.status_label}
                  </Text>
                  <Text fw={700}>{formatTRY(o.total_minor)}</Text>
                </Group>

                {isPending ? (
                  <Group gap={6} wrap="nowrap">
                    <Loader size="xs" color="yellow" />
                    <Text size="xs" c="dimmed">
                      Garson siparişinizi onayladığında hazırlanmaya başlanacak.
                    </Text>
                  </Group>
                ) : (
                  <OrderProgress step={o.status_step} brand={brand} />
                )}

                <Divider />

                {/* Sipariş satırları */}
                <Stack gap={4}>
                  {o.lines.map((ln: any) => (
                    <Group key={ln.id} justify="space-between" wrap="nowrap">
                      <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" lineClamp={1}>
                          {ln.quantity}× {ln.product_name}
                        </Text>
                        {ln.comp_quantity_approved > 0 && (
                          <Badge size="xs" color="pink" variant="light" style={{ flexShrink: 0 }}>
                            🎁 {ln.comp_quantity_approved} ikram
                          </Badge>
                        )}
                      </Group>
                      <Text size="sm" c="dimmed">
                        {formatTRY(ln.line_total_minor)}
                      </Text>
                    </Group>
                  ))}
                </Stack>

                {/* Garson onayı beklerken müşteri vazgeçebilir. Onaylandıktan
                    sonra (pending değil) buton kaybolur — Order yaratılmıştır,
                    iptal için garsona danışmak gerekir. */}
                {isPending && (
                  <Button
                    variant="subtle"
                    color="red"
                    size="xs"
                    loading={cancelRequest.isPending}
                    onClick={() => {
                      cancelRequest.mutate(
                        { requestId: o.id, qr_token: token },
                        {
                          onSuccess: () =>
                            notifications.show({
                              message: "Siparişiniz iptal edildi",
                              color: "orange",
                            }),
                          onError: (e: any) =>
                            notifications.show({
                              message:
                                e?.response?.status === 409
                                  ? "Sipariş garson tarafından onaylandı — iptal için garsonunuza danışın."
                                  : e?.response?.data?.detail || "İptal edilemedi",
                              color: "red",
                            }),
                        },
                      );
                    }}
                  >
                    Siparişi İptal Et
                  </Button>
                )}
              </Stack>
            </Card>
            );
          })}
        </Stack>
      </Container>

      {/* Alt sabit aksiyon çubuğu — menüye dön + hesap iste */}
      <Box
        p="md"
        style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: "white", boxShadow: "0 -2px 8px rgba(0,0,0,0.1)", zIndex: 100,
        }}
      >
        <Group grow>
          <Button variant="default" size="lg" onClick={onBackToMenu}>
            {t("qr.back_to_menu", "Menüye Dön")}
          </Button>
          {openOrders.length > 0 && (
            <Button
              size="lg"
              style={{ background: brand }}
              onClick={() => setPayOpen(true)}
            >
              {t("qr.request_bill", "Hesabı İste")}
            </Button>
          )}
        </Group>
      </Box>

      {/* Ödeme isteği modalı — tek / parçalı */}
      <PaymentModal
        opened={payOpen}
        onClose={() => setPayOpen(false)}
        brand={brand}
        totalMinor={openTotal}
        onConfirm={async (split) => {
          try {
            const res = await requestPayment.mutateAsync({
              qr_token: token,
              split,
            });
            notifications.show({
              message: res?.message || "Garson çağrıldı",
              color: "green",
            });
            setPayOpen(false);
          } catch (e: any) {
            notifications.show({
              message: e?.response?.data?.detail || "İstek gönderilemedi",
              color: "red",
            });
          }
        }}
        loading={requestPayment.isPending}
      />
    </Box>
  );
}

// ===========================================================================
// Ödeme modalı — tek ödeme veya parçalı (bölüşmeli)
// ===========================================================================
function PaymentModal({
  opened, onClose, brand, totalMinor, onConfirm, loading,
}: {
  opened: boolean;
  onClose: () => void;
  brand: string;
  totalMinor: number;
  onConfirm: (split: { mode: "single" | "split"; parts: number }) => void;
  loading: boolean;
}) {
  const { t } = useI18n();
  const [mode, setMode] = useState<"single" | "split">("single");
  const [parts, setParts] = useState(2);

  const perPart = parts > 0 ? Math.floor(totalMinor / parts) : totalMinor;

  return (
    <Modal opened={opened} onClose={onClose} title={t("qr.payment_title", "Hesap / Ödeme")} size="sm" radius="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Text c="dimmed">{t("qr.total_amount", "Toplam Tutar")}</Text>
          <Text fw={800} size="xl" style={{ color: brand }}>
            {formatTRY(totalMinor)}
          </Text>
        </Group>

        <Divider label={t("qr.payment_method", "Ödeme şekli")} labelPosition="center" />

        {/* Tek / parçalı seçimi */}
        <Group grow>
          <Button
            variant={mode === "single" ? "filled" : "default"}
            style={mode === "single" ? { background: brand } : undefined}
            onClick={() => setMode("single")}
          >
            {t("qr.pay_single", "Tek Ödeme")}
          </Button>
          <Button
            variant={mode === "split" ? "filled" : "default"}
            style={mode === "split" ? { background: brand } : undefined}
            onClick={() => setMode("split")}
          >
            {t("qr.pay_split", "Parçalı (Bölüşmeli)")}
          </Button>
        </Group>

        {/* Parçalı: kaç kişi? */}
        {mode === "split" && (
          <Stack gap={6}>
            <Text size="sm" fw={600}>{t("qr.split_question", "Kaç parçaya bölünsün?")}</Text>
            <Group gap="xs" justify="center">
              <ActionIcon
                size="lg"
                variant="default"
                onClick={() => setParts((p) => Math.max(2, p - 1))}
              >
                <IconMinus size={16} />
              </ActionIcon>
              <Badge size="xl" variant="filled" style={{ background: brand }}>
                {parts} {t("qr.people", "kişi")}
              </Badge>
              <ActionIcon
                size="lg"
                variant="default"
                onClick={() => setParts((p) => Math.min(20, p + 1))}
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Group>
            <Text size="sm" c="dimmed" ta="center">
              {t("qr.per_person", "Kişi başı yaklaşık")} <strong>{formatTRY(perPart)}</strong>
            </Text>
          </Stack>
        )}

        <Text size="xs" c="dimmed" ta="center">
          {t("qr.payment_note", "Onayladığınızda garson çağrılır. Ödemeyi garson masada tahsil eder.")}
        </Text>

        <Button
          size="lg"
          fullWidth
          loading={loading}
          style={{ background: brand }}
          onClick={() => onConfirm({ mode, parts: mode === "split" ? parts : 1 })}
        >
          {t("qr.call_waiter", "Garsonu Çağır")}
        </Button>
      </Stack>
    </Modal>
  );
}

// ===========================================================================
// App
// ===========================================================================
export function App() {
  const [token] = useState(() => getTokenFromUrl());
  const [step, setStep] = useState<Step>("landing");
  // Sepet masa bazında sessionStorage'da tutulur — mobil tarayıcı arka
  // plandaki sekmeyi yeniden yüklediğinde / müşteri yenilediğinde kaybolmasın.
  const cartKey = `qr_cart:${token ?? ""}`;
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const raw = sessionStorage.getItem(cartKey);
      return raw ? (JSON.parse(raw) as CartItem[]) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      if (cart.length) sessionStorage.setItem(cartKey, JSON.stringify(cart));
      else sessionStorage.removeItem(cartKey);
    } catch {
      /* storage kapalı (gizli mod) — bellekte devam */
    }
  }, [cart, cartKey]);
  const [loginOpen, setLoginOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [reservationOpen, setReservationOpen] = useState(false);
  const [customer, setCustomer] = useState<CustomerSession | null>(() => {
    const t = localStorage.getItem("customer_token");
    const id = localStorage.getItem("customer_id");
    if (!t || !id) return null;
    return { customer_token: t, customer_id: id, display_name: "", phone: "" };
  });
  const createOrder = useCreateQrOrder();
  const qc = useQueryClient();

  const { lang } = useI18n();
  const resolve = useResolveQrToken(token);
  const menu = usePublicMenu(token, lang);
  const brand = safeBrand(
    menu.data?.tenant?.brand_color || resolve.data?.tenant?.brand_color,
  );
  const accent = safeAccent(
    menu.data?.tenant?.accent_color || resolve.data?.tenant?.accent_color,
    brand,
  );
  // Landing markası — resolve hızlı döner, logo/arka plan menüden de gelir.
  const tenantBrand = {
    logo: resolve.data?.tenant?.logo || menu.data?.tenant?.logo || null,
    background:
      resolve.data?.tenant?.menu_background_image ||
      menu.data?.tenant?.menu_background_image ||
      null,
  };

  useEffect(() => {
    document.documentElement.style.setProperty("--qr-brand", brand);
  }, [brand]);

  function handleLogout() {
    localStorage.removeItem("customer_token");
    localStorage.removeItem("customer_id");
    setCustomer(null);
    notifications.show({ message: "Çıkış yapıldı", color: "blue" });
  }

  // Sayfa yenilenince session localStorage'dan restore edilir ama avatar/ad
  // taşımaz → üst bar avatarı boş kalır. Token varsa profili bir kez çekip
  // display_name + avatar'ı session'a doldur (üst bar avatarı dolsun).
  const customerToken = customer?.customer_token;
  useEffect(() => {
    if (!customerToken) return;
    let cancelled = false;
    axios
      .get(`${MENU_API_URL}/me/profile/`, {
        headers: { Authorization: `Bearer ${customerToken}` },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setCustomer((c) =>
          c
            ? {
                ...c,
                display_name: data.display_name || c.display_name,
                phone: data.phone || c.phone,
                avatar: data.avatar || undefined,
              }
            : c,
        );
      })
      .catch(() => {
        /* profil çekilemezse üst bar baş harfe düşer — sessiz geç */
      });
    return () => {
      cancelled = true;
    };
  }, [customerToken]);

  if (!token) {
    return (
      <Center mih="100vh" p="md">
        <Alert color="red" icon={<IconAlertCircle size={20} />} title="QR token eksik">
          Lütfen masadaki QR kodu okutarak girin.
        </Alert>
      </Center>
    );
  }

  async function handleCheckout() {
    try {
      await createOrder.mutateAsync({
        qr_token: token!,
        // c.id sepet satır kimliğidir (ürün+modifier); backend'e gerçek
        // ürün id'si (productId) ve seçili modifier'lar ayrı gider.
        lines: cart.map((c) => ({
          product_id: c.productId,
          quantity: c.qty,
          modifier_ids: c.modifierIds,
        })),
        customer_phone: customer?.phone || "",
        customer_name: customer?.display_name || "",
      });
      notifications.show({ message: "Sipariş garson onayına gönderildi", color: "green" });
      setCart([]);
      // Takip ekranına geçmeden önce sipariş listesini tazele — aksi halde
      // Tracking, cache'deki yeni sipariş yokken eski veriyi gösterir
      // (WS push'a güvenmek timing'e bağlı: ekran zaten açıkken WS OPEN
      // kaldığından reconnect-invalidate effect'i tetiklenmez).
      await qc.invalidateQueries({ queryKey: ["qr-table-orders", token] });
      setStep("tracking");
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Sipariş gönderilemedi",
        color: "red",
      });
    }
  }

  return (
    <>
      {step === "landing" && (
        <Landing
          token={token}
          brand={brand}
          logo={tenantBrand.logo}
          background={tenantBrand.background}
          onStart={() => setStep("menu")}
          onReservation={() => setReservationOpen(true)}
        />
      )}
      {step === "menu" && (
        <MenuView
          token={token}
          brand={brand}
          accent={accent}
          cart={cart}
          setCart={setCart}
          onCheckout={handleCheckout}
          customer={customer}
          onLogin={() => setLoginOpen(true)}
          onLogout={handleLogout}
          onAccount={() => setAccountOpen(true)}
          onShowOrders={() => setStep("tracking")}
        />
      )}
      {step === "tracking" && (
        <Tracking
          token={token}
          brand={brand}
          onBackToMenu={() => setStep("menu")}
        />
      )}

      <CustomerLoginModal
        opened={loginOpen}
        onClose={() => setLoginOpen(false)}
        qrToken={token}
        onLoginSuccess={(data) => {
          setCustomer(data);
          notifications.show({
            message: `Hoş geldiniz ${data.display_name || data.phone}!`,
            color: "green",
          });
        }}
      />

      <MyAccountDrawer
        opened={accountOpen}
        onClose={() => setAccountOpen(false)}
        customerName={customer?.display_name || customer?.phone || ""}
        onLogout={handleLogout}
        onProfileUpdate={(p) =>
          setCustomer((c) =>
            c
              ? (({ avatar: _old, ...rest }) => ({
                  ...rest,
                  display_name: p.display_name || c.display_name,
                  ...(p.avatar ? { avatar: p.avatar } : {}),
                }))(c)
              : c,
          )
        }
      />

      <ReservationModal
        opened={reservationOpen}
        onClose={() => setReservationOpen(false)}
        qrToken={token}
      />
    </>
  );
}
